import React, { useCallback, useMemo, useState } from 'react'

import { useMutation } from '@apollo/client'
import clsx from 'clsx'
import groupBy from 'lodash/groupBy'

import Toast from '$extensionSrc/components/Toast'
import {
  EmailCategorySettingUpdate,
  EmailSetting,
  EmailSingleSettingUpdate,
} from '$extensionSrc/notifications/notificationTypes'

import NotificationCategorySettings from './NotificationCategorySettings'
import styles from './NotificationSettingsPage.module.scss'
import { client } from '../apolloClient'
import { updateNotificationSettingsMutation } from '../data/query'

export default function NotificationSettingsPage({
  currentUserId,
  notificationSettings: initialNotificationSettings,
}: {
  currentUserId: string
  notificationSettings: EmailSetting[]
}) {
  const [categoryBeingSaved, setCategoryBeingSaved] = useState<string | null>(
    null,
  )

  const [notificationSettings, setNotificationSettings] = useState<
    EmailSetting[]
  >(initialNotificationSettings)

  const [submitUpdateNotificationSettingsRequest, { error }] = useMutation(
    updateNotificationSettingsMutation,
    {
      client,
      onCompleted: () => {
        setCategoryBeingSaved(null)
      },
      onError: () => {
        setCategoryBeingSaved(null)
      },
    },
  )

  const handleSingleSettingUpdate = useCallback(
    ({ category, settingId, targetValue }: EmailSingleSettingUpdate) => {
      setCategoryBeingSaved(category)
      setNotificationSettings((currentNotificationSettings) => {
        const updatedNotificationSettings = currentNotificationSettings.map(
          (s) => {
            if (settingId !== s.id) {
              return s
            }
            return {
              ...s,
              settingValue: targetValue,
            }
          },
        )
        submitUpdateNotificationSettingsRequest({
          variables: {
            userId: currentUserId,
            notificationSettings: updatedNotificationSettings.map((s) =>
              JSON.stringify(s),
            ),
          },
        })
        return updatedNotificationSettings
      })
    },
    [currentUserId, submitUpdateNotificationSettingsRequest],
  )

  const handleCategoryChannelSettingUpdate = useCallback(
    ({ category, targetValue }: EmailCategorySettingUpdate) => {
      setCategoryBeingSaved(category)
      setNotificationSettings((currentNotificationSettings) => {
        const updatedNotificationSettings = currentNotificationSettings.map(
          (s) => {
            if (category !== s.category) {
              return s
            }
            return {
              ...s,
              settingValue: targetValue,
            }
          },
        )
        submitUpdateNotificationSettingsRequest({
          variables: {
            userId: currentUserId,
            notificationSettings: updatedNotificationSettings.map((s) =>
              JSON.stringify(s),
            ),
          },
        })
        return updatedNotificationSettings
      })
    },
    [currentUserId, submitUpdateNotificationSettingsRequest],
  )

  const notificationSettingsByCategory: Record<string, EmailSetting[]> =
    useMemo(
      () => groupBy(notificationSettings, 'category'),
      [notificationSettings],
    )

  return (
    <div className={clsx(styles.NotificationSettingsPage)}>
      <div className="title heading1 bold">Email Preferences</div>
      <div className="subtitle body1 darkest-gray center">
        Customize your email preferences to stay informed on what matters most
        to you.
      </div>
      {Object.entries(notificationSettingsByCategory).map(
        ([category, settings]) => (
          <NotificationCategorySettings
            category={category}
            categoryBeingSaved={categoryBeingSaved}
            key={category}
            onCategoryChannelSettingUpdate={handleCategoryChannelSettingUpdate}
            onSingleSettingUpdate={handleSingleSettingUpdate}
            settings={settings}
            updateError={error}
          />
        ),
      )}
      {error && (
        <Toast
          text={`${error.message} - Make sure you're logged in or try again later.`}
          color="red"
          noAutoDismiss
          showCloseIcon
        />
      )}
    </div>
  )
}
